/**
 * Sysadmin Runtime → Health (Wave 3 §10.2 #6 / spec §3.5).
 *
 * Real grid view of platform service health. Pulls from
 * GET /admin/platform/runtime/services every 10 s. Backed by the
 * service_health_snapshots table (W1b-18); the sysadmin endpoint
 * (added §10.2 #6) returns the cross-tenant DISTINCT-ON snapshot
 * without the per-tenant maintenance overlay.
 *
 * v1 ships:
 *   - 13 service tiles (canonical SERVICE_NAMES list)
 *   - status pill (healthy / degraded / down / unknown)
 *   - latency, version, last-probed timestamp
 *   - error message excerpt for degraded / down
 *   - top-level summary tile (X / 13 healthy)
 *   - manual refresh button
 *
 * v2 (Wave 4): per-service detail drawer with rolling p50/p95/p99,
 * restart action, SSE log tail, maintenance window scheduler.
 */

import { createFileRoute } from '@tanstack/react-router';
import { useMemo } from 'react';
import {
  Heart, RefreshCw, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Clock,
} from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { StatTile, type StatTileTone } from '@/components/ui/stat-tile';
import { PageError } from '@/components/ui/page-error';
import {
  useSysadminServices,
  type ServiceStatus,
  type ServiceHealthRow,
} from '@/lib/use-sysadmin-runtime';
import { cn } from '@/lib/utils';

// Map ServiceStatus → StatTile tone (PR-0 §11.12 single semantic palette)
const STATUS_TONE: Record<ServiceStatus, StatTileTone> = {
  healthy: 'success',
  degraded: 'warning',
  down: 'destructive',
  unknown: 'muted',
};

export const Route = createFileRoute('/sysadmin/runtime/health')({
  component: RuntimeHealthPage,
});

const STATUS_META: Record<
  ServiceStatus,
  { label: string; icon: typeof CheckCircle2; tone: string; tile: string }
> = {
  healthy: {
    label: 'Healthy',
    icon: CheckCircle2,
    tone: 'text-green-600 dark:text-green-400',
    tile: 'border-green-500/20 bg-green-500/5',
  },
  degraded: {
    label: 'Degraded',
    icon: AlertTriangle,
    tone: 'text-amber-600 dark:text-amber-400',
    tile: 'border-amber-500/30 bg-amber-500/5',
  },
  down: {
    label: 'Down',
    icon: XCircle,
    tone: 'text-destructive',
    tile: 'border-destructive/30 bg-destructive/5',
  },
  unknown: {
    label: 'Unknown',
    icon: HelpCircle,
    tone: 'text-muted-foreground',
    tile: 'border-muted bg-muted/20',
  },
};

function RuntimeHealthPage() {
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } =
    useSysadminServices();

  const summary = useMemo(() => {
    if (!data) return { healthy: 0, degraded: 0, down: 0, unknown: 0, total: 0 };
    const c = { healthy: 0, degraded: 0, down: 0, unknown: 0, total: data.services.length };
    for (const s of data.services) c[s.status] += 1;
    return c;
  }, [data]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <Heart className="h-7 w-7 text-muted-foreground" />
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Service Health</h1>
            <div className="flex items-center gap-2 mt-1">
              <Badge variant="outline" className="text-xs font-mono">§3.5</Badge>
              <Badge variant="secondary" className="text-xs">v1</Badge>
              {dataUpdatedAt > 0 && (
                <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Updated {new Date(dataUpdatedAt).toLocaleTimeString()}
                </span>
              )}
            </div>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetch()}
          disabled={isFetching}
        >
          <RefreshCw className={cn('h-3.5 w-3.5 mr-1.5', isFetching && 'animate-spin')} />
          Refresh
        </Button>
      </div>

      {/* Summary strip — canonical StatTile (§11.3) */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatTile
          label="Healthy"
          value={summary.healthy}
          suffix={`/ ${summary.total}`}
          icon={STATUS_META.healthy.icon}
          tone={STATUS_TONE.healthy}
        />
        <StatTile
          label="Degraded"
          value={summary.degraded}
          suffix={`/ ${summary.total}`}
          icon={STATUS_META.degraded.icon}
          tone={STATUS_TONE.degraded}
        />
        <StatTile
          label="Down"
          value={summary.down}
          suffix={`/ ${summary.total}`}
          icon={STATUS_META.down.icon}
          tone={STATUS_TONE.down}
        />
        <StatTile
          label="Unknown"
          value={summary.unknown}
          suffix={`/ ${summary.total}`}
          icon={STATUS_META.unknown.icon}
          tone={STATUS_TONE.unknown}
        />
      </div>

      {/* Body */}
      {isLoading ? (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground animate-pulse">
            Probing services…
          </CardContent>
        </Card>
      ) : error ? (
        <PageError
          title="Failed to load service snapshot"
          message={error.message}
          onRetry={() => refetch()}
          retrying={isFetching}
        />
      ) : data ? (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">All services</CardTitle>
            <CardDescription>
              Sourced from <code>service_health_snapshots</code> (the
              W1b-18 probe pipeline). Per-tenant maintenance windows
              are not overlaid here — they live on the tenant-side
              <code> /admin/health</code> view.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {data.services.map((s) => (
              <ServiceTile key={s.name} service={s} />
            ))}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

// ─── ServiceTile ─────────────────────────────────────────────────────────────

function ServiceTile({ service }: { service: ServiceHealthRow }) {
  const meta = STATUS_META[service.status];
  const Icon = meta.icon;
  const probedAgo = service.lastProbedAt
    ? secondsSince(service.lastProbedAt)
    : null;

  return (
    <div className={cn('rounded border p-3 space-y-2', meta.tile)}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-sm truncate" title={service.name}>
            {service.name}
          </div>
          {service.version && (
            <div className="text-[11px] text-muted-foreground font-mono mt-0.5">
              {service.version}
            </div>
          )}
        </div>
        <Badge
          variant="outline"
          className={cn('text-[10px] gap-1 shrink-0', meta.tone)}
        >
          <Icon className="h-3 w-3" />
          {meta.label}
        </Badge>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Latency</span>
        <span className="font-mono tabular-nums">
          {service.latencyMs !== null ? `${service.latencyMs} ms` : '—'}
        </span>
      </div>

      <div className="flex items-center justify-between text-xs">
        <span className="text-muted-foreground">Last probe</span>
        <span className="font-mono tabular-nums">
          {probedAgo !== null ? formatAgo(probedAgo) : '—'}
        </span>
      </div>

      {service.errorMessage && (
        <div className="text-[11px] text-destructive bg-destructive/10 rounded p-2 line-clamp-3">
          {service.errorMessage}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function secondsSince(iso: string): number {
  const now = Date.now();
  const t = new Date(iso).getTime();
  return Math.max(0, Math.round((now - t) / 1000));
}

function formatAgo(seconds: number): string {
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)}h ago`;
  return `${Math.round(seconds / 86400)}d ago`;
}
